/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue, DragSourceBox } from './DragSourceBox';
import { DragSourceProps } from '../DragSource/DragSourceManager';
import { BoxProps, isPropertyVisible } from '../Box/BoxExports';

const DragSourceBoxManager: ComponentManagerFn<'DragSourceBox'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'DragSourceBox'>(platform, 'DragSourceBox'),
    type: 'DragSourceBox',
    name: 'Drag Source Box',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'Box',
          'Icon',
          'IconButton',
          'IconSwitch',
          'PulseTip',
          'Text',
          'DropTargetBox',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      ...DragSourceProps,
      ...BoxProps,
    },
    isPropertyVisible: <K extends keyof DragSourceBox>(
      property: K,
      itemData: Required<DragSourceBox>,
    ) => {
      switch (property) {
        case 'type':
        case 'beginDrag':
        case 'canDragCondition':
        case 'canDrag': {
          return true;
        }
        default: {
          return isPropertyVisible(property, itemData);
        }
      }
    },
    category: 'Dashboard',
    replaceableWith: ['Box', 'DropTargetBox'],
    icon: { iconName: 'box' },
    description: `**Overview**<br/>
The Drag Source Box is a hybrid container that combines the visual layout capabilities of a standard Box with the interactive behavior of a Drag Source.
In standard drag-and-drop implementations, you typically wrap a visual element inside an invisible "Drag Source" container. This component simplifies that workflow by merging them into one. It allows you to create fully styled, draggable UI elements—like Kanban cards, inventory items, or dashboard widgets—without adding unnecessary layers to your component tree.

**1. Drag Configuration (The Logic)**<br/>
While the component looks like a Box, its primary function is to be moved. You must define how and where it can be moved using these properties.
* **Type:** A mandatory string identifier (e.g., 'task-card', 'widget'). This acts as a security key (The Handshake); this component can only be dropped onto a Drop Target that is configured with this exact same Type. This prevents logical errors, like dropping a "User" onto a "Date Picker."
* **Can Drag Condition:** A boolean expression or logic rule that enables or disables the drag capability. (Use Case: Use this to lock items in place based on state, e.g., "Prevent dragging if the Status is 'Archived'").
* **beginDrag Function:** A controller function that triggers the moment the user picks up the item. (Use Case: Use this to prepare data, e.g., store the card's ID so the Drop Target knows what it is receiving).

**2. Visual Layout (The Box)**<br/>
Visually, the Drag Source Box functions exactly like a standard Box component. It provides full access to CSS-based styling and Flexbox layout controls.
* **Layout & Spacing:** Control Width, Height, Padding, Margin, and Flex Direction to arrange the content inside the draggable area.
* **Styling:** Customize Background Color, Borders, and Shadows to make the draggable item look like a card, a button, or a list item.

**3. Composition & Content**<br/>
Because it serves as a wrapper for interactive elements, the Drag Source Box supports a specific set of child components designed to build rich UI tiles.
* **Allowed Children:** Visuals (Box, Icon, Text, PulseTip), Interactions (IconButton, IconSwitch), and Nested Logic (DropTargetBox).

**4. Interchangeability**<br/>
To facilitate rapid iteration, this component shares a lineage with other Box types.
* **Replaceable:** You can convert a Drag Source Box directly into a standard Box (removing drag logic) or a Drop Target Box (inverting the logic from "giving" to "receiving") using the designer's replace functionality.`,
  };
};

export default DragSourceBoxManager;
